import React, { useState } from 'react'
import {
  Flex,
  Box,
  Text,
  LoadingPlaceholder,
  Checkbox,
  handleFetchMore,
  InfiniteList,
  Input,
} from '@sketch/components'
import { FetchMoreFunc } from '@sketch/components/src/InfiniteList/InfiniteList'
import {
  ListContainer,
  PageIcon,
  AllPrototypesIcon,
  PrototypeIcon,
  SearchContainer,
  SearchActions,
  SearchButton,
} from './ContentsTab.styles'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  useGetPagesQuery,
  useGetFramesForDocumentQuery,
} from '@sketch/gql-types'
import { usePreviewForm } from './usePreviewForm'
import { capitalize } from 'lodash'

interface ContentsTabProps {
  shareIdentifier: string
  versionShortId: string
}

const ENTRIES_PATH = ['share', 'version', 'document', 'pages', 'entries']

type ListItem = { name: string; uuid: string }
type SelectedItem = { identifier: string; type: 'PAGE' | 'PROTOTYPE' }

interface ItemListProps {
  handleSelectAll: () => void
  toggleUUID: (id: string, type: 'PAGE' | 'PROTOTYPE') => void
  items: ListItem[]
  after: string | null
  fetchMore: FetchMoreFunc
  selectedUUIDs: SelectedItem[]
  allChecked: boolean
  type: 'prototypes' | 'pages'
}

const ItemList = ({
  handleSelectAll,
  items,
  after,
  fetchMore,
  toggleUUID,
  allChecked,
  selectedUUIDs,
  type,
}: ItemListProps) => {
  if (items.length === 0) {
    // @TODO: add empty state
    return <>No {type} found</>
  }

  return (
    <>
      <Box mb={2}>
        <Checkbox
          name={`all${type}`}
          checked={allChecked}
          onChange={handleSelectAll}
        >
          <Flex alignItems="center">
            {type === 'pages' ? <PageIcon /> : <AllPrototypesIcon />}
            All {capitalize(type)}
          </Flex>
        </Checkbox>
      </Box>
      <ListContainer>
        <InfiniteList
          wrappingComponent={React.Fragment}
          renderLoading={() => <LoadingPlaceholder size="64px" />}
          canLoadMore={after !== null}
          onLoadMore={handleFetchMore(fetchMore, ENTRIES_PATH, {
            dataIdFromObject,
            after: after,
          })}
        >
          {items.map(({ uuid, name }) =>
            uuid !== null ? (
              <Box mb={2} key={uuid}>
                <Checkbox
                  name="selectedUUIDs"
                  checked={selectedUUIDs.some(item => item.identifier === uuid)}
                  onChange={() =>
                    toggleUUID(uuid, type === 'pages' ? 'PAGE' : 'PROTOTYPE')
                  }
                >
                  <Flex alignItems="center">
                    {type === 'pages' ? <PageIcon /> : <PrototypeIcon />}
                    {name}
                  </Flex>
                </Checkbox>
              </Box>
            ) : null
          )}
        </InfiniteList>
      </ListContainer>
    </>
  )
}

export const ContentsTab = ({
  shareIdentifier,
  versionShortId,
}: ContentsTabProps) => {
  const { values, setFieldValue } = usePreviewForm()
  const selectedUUIDs: SelectedItem[] = values.selectedUUIDs || []
  const [searchTerm, setSearchTerm] = useState('')

  const { data, loading, error, fetchMore } = useGetPagesQuery({
    variables: {
      shareIdentifier,
      versionShortId,
    },
  })

  const prototypeFramesQuery = useGetFramesForDocumentQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      search: searchTerm,
      filter: 'PROTOTYPE',
    },
  })

  const handleAddUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    if (!selectedUUIDs.some(item => item.identifier === id)) {
      setFieldValue('selectedUUIDs', [
        ...selectedUUIDs,
        { identifier: id, type },
      ])
    }
  }

  const handleRemoveUUID = (id: string) => {
    setFieldValue(
      'selectedUUIDs',
      selectedUUIDs.filter(item => item.identifier !== id)
    )
  }

  const toggleUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    if (selectedUUIDs.some(item => item.identifier === id)) {
      handleRemoveUUID(id)
    } else {
      handleAddUUID(id, type)
    }
  }

  const handleSelectAllPages = () => {
    const areAllFilteredPagesSelected = filteredPageIds.every(id =>
      selectedUUIDs.some(item => item.identifier === id)
    )
    if (areAllFilteredPagesSelected) {
      // Deselect filtered pages
      setFieldValue(
        'selectedUUIDs',
        selectedUUIDs.filter(item => !filteredPageIds.includes(item.identifier))
      )
    } else {
      // Select all filtered pages (keep existing prototypes and other pages)
      const newPageItems = filteredPageIds
        .filter(id => !selectedUUIDs.some(item => item.identifier === id))
        .map(id => ({ identifier: id, type: 'PAGE' as const }))
      setFieldValue('selectedUUIDs', [...selectedUUIDs, ...newPageItems])
    }
  }

  const handleSelectAllPrototypes = () => {
    const areAllPrototypesSelected = allPrototypeIds.every(id =>
      selectedUUIDs.some(item => item.identifier === id)
    )
    if (areAllPrototypesSelected) {
      // Deselect all prototypes
      setFieldValue(
        'selectedUUIDs',
        selectedUUIDs.filter(item => !allPrototypeIds.includes(item.identifier))
      )
    } else {
      // Select all prototypes (keep existing pages)
      const newPrototypeItems = allPrototypeIds
        .filter(id => !selectedUUIDs.some(item => item.identifier === id))
        .map(id => ({ identifier: id, type: 'PROTOTYPE' as const }))
      setFieldValue('selectedUUIDs', [...selectedUUIDs, ...newPrototypeItems])
    }
  }

  const handleGlobalSelectAll = () => {
    const allItems = [
      ...allPageIds.map(id => ({ identifier: id, type: 'PAGE' as const })),
      ...allPrototypeIds.map(id => ({
        identifier: id,
        type: 'PROTOTYPE' as const,
      })),
    ]
    setFieldValue('selectedUUIDs', allItems)
  }

  const handleGlobalDeselectAll = () => {
    setFieldValue('selectedUUIDs', [])
  }

  if (error || prototypeFramesQuery.error) {
    // @TODO: add error case
    return (
      <>
        <Text mb={3}>
          Select the prototypes or pages that will be available in this preview.
        </Text>
        <Flex justifyContent="center" height={543}>
          An error occured
        </Flex>
      </>
    )
  }

  const afterPages = data?.share?.version?.document?.pages?.meta.after || null
  const afterPrototypes =
    prototypeFramesQuery.data?.share?.version?.document?.frames.meta.after ||
    null

  const formatEntry = (entries: any[]) =>
    entries
      .map(entry => ({
        name: entry.name,
        uuid: entry.uuid,
      }))
      .filter(
        (page): page is ListItem => page.name !== null && page.uuid !== null
      ) ?? []

  const allPages = formatEntry(
    data?.share?.version?.document?.pages?.entries || []
  )
  const allPrototypes = formatEntry(
    prototypeFramesQuery.data?.share?.version?.document?.frames.entries || []
  )

  // Filter pages based on search term
  const pages = searchTerm
    ? allPages.filter(page =>
        page.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allPages

  const filteredPageIds = pages.map(entry => entry.uuid)
  const allPageIds = allPages.map(entry => entry.uuid)
  const allPrototypeIds = allPrototypes.map(entry => entry.uuid)

  const isLoading = !!(loading || prototypeFramesQuery.loading)

  return (
    <>
      <Text mb={3}>
        Select the prototypes or pages that will be available in this preview.
      </Text>

      <SearchContainer>
        <Input
          type="text"
          placeholder="Search…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <SearchActions>
          <SearchButton
            loading={isLoading}
            variant="primary"
            disabled={
              selectedUUIDs.length ===
              allPageIds.length + allPrototypeIds.length
            }
            onClick={handleGlobalSelectAll}
          >
            Select All
          </SearchButton>
          <SearchButton
            loading={isLoading}
            disabled={selectedUUIDs.length === 0}
            variant="primary"
            onClick={handleGlobalDeselectAll}
          >
            Deselect All
          </SearchButton>
        </SearchActions>
      </SearchContainer>

      {isLoading ? (
        <Flex justifyContent="center" height={384}>
          <LoadingPlaceholder />
        </Flex>
      ) : (
        <Box height={380}>
          <Box mt={5}>
            <ItemList
              type="prototypes"
              handleSelectAll={handleSelectAllPrototypes}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={allPrototypes}
              selectedUUIDs={selectedUUIDs}
              after={afterPrototypes}
              allChecked={allPrototypeIds.every(id =>
                selectedUUIDs.some(item => item.identifier === id)
              )}
            />
          </Box>
          <Box mt={5}>
            <ItemList
              type="pages"
              handleSelectAll={handleSelectAllPages}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={pages}
              selectedUUIDs={selectedUUIDs}
              after={afterPages}
              allChecked={filteredPageIds.every(id =>
                selectedUUIDs.some(item => item.identifier === id)
              )}
            />
          </Box>
        </Box>
      )}
    </>
  )
}
